import { createApi } from '@reduxjs/toolkit/query/react';
import { buildBaseQuery } from 'app/api';
import {
  Worker,
  CreateWorkerRequest,
  UpdateWorkerRequest,
  WorkerEmployer,
  CreateWorkerEmployerRequest,
  WorkerDocument,
  WorkerDocumentType,
  CreateWorkerDocumentRequest,
  UpdateWorkerDocumentRequest,
} from 'app/types/worker';

const name = 'workersApi';

const tags = {
  WORKER_LIST: 'WorkerList',
  WORKER: 'Worker',
  EMPLOYERS: 'WorkerEmployers',
  DOCUMENT_TYPES: 'WorkerDocumentTypes',
  DOCUMENTS: 'WorkerDocuments',
};

export const workersApi = createApi({
  reducerPath: name,
  baseQuery: buildBaseQuery('api/workers'),
  tagTypes: Object.values(tags),
  endpoints: (builder) => ({
    // ===== WORKERS =====
    getWorkers: builder.query<Worker[], { unitId: number; isActive?: boolean }>({
      query: ({ unitId, isActive }) => {
        let url = `/${unitId}`;
        if (isActive !== undefined) {
          url += `?isActive=${isActive}`;
        }
        return url;
      },
      providesTags: [ tags.WORKER_LIST ],
    }),
    getWorkerById: builder.query<Worker, number>({
      query: (workerId) => `/${workerId}/detail`,
      providesTags: [ tags.WORKER ],
    }),
    createWorker: builder.mutation<Worker, CreateWorkerRequest>({
      query: (body) => ({
        url: '/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [ tags.WORKER_LIST ],
    }),
    updateWorker: builder.mutation<Worker, UpdateWorkerRequest>({
      query: (body) => ({
        url: '/update',
        method: 'PUT',
        body,
      }),
      invalidatesTags: [ tags.WORKER_LIST, tags.WORKER ],
    }),
    uploadWorkerPhoto: builder.mutation<Worker, { workerId: number; file: File }>({
      query: ({ workerId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/${workerId}/upload/photo`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: [ tags.WORKER_LIST, tags.WORKER ],
    }),
    deleteWorker: builder.mutation<void, number>({
      query: (workerId) => ({
        url: `/${workerId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [ tags.WORKER_LIST ],
    }),

    // ===== EMPLOYERS =====
    getEmployers: builder.query<WorkerEmployer[], number>({
      query: (unitId) => `/${unitId}/employers`,
      providesTags: [ tags.EMPLOYERS ],
    }),
    createEmployer: builder.mutation<WorkerEmployer, CreateWorkerEmployerRequest>({
      query: (body) => ({
        url: '/employers/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [ tags.EMPLOYERS ],
    }),

    // ===== DOCUMENTS =====
    getDocumentTypes: builder.query<WorkerDocumentType[], number>({
      query: (unitId) => `/${unitId}/document-types`,
      providesTags: [ tags.DOCUMENT_TYPES ],
    }),
    getDocuments: builder.query<WorkerDocument[], number>({
      query: (workerId) => `/${workerId}/documents`,
      providesTags: [ tags.DOCUMENTS ],
    }),
    createDocument: builder.mutation<WorkerDocument, CreateWorkerDocumentRequest>({
      query: (body) => ({
        url: '/documents',
        method: 'POST',
        body,
      }),
      invalidatesTags: [ tags.DOCUMENTS, tags.WORKER, tags.WORKER_LIST ],
    }),
    updateDocument: builder.mutation<WorkerDocument, UpdateWorkerDocumentRequest>({
      query: (body) => ({
        url: '/documents',
        method: 'PUT',
        body,
      }),
      invalidatesTags: [ tags.DOCUMENTS, tags.WORKER, tags.WORKER_LIST ],
    }),
    uploadDocumentFile: builder.mutation<WorkerDocument, { documentId: number; file: File }>({
      query: ({ documentId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/documents/${documentId}/upload`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: [ tags.DOCUMENTS ],
    }),
    deleteDocument: builder.mutation<void, number>({
      query: (documentId) => ({
        url: `/documents/${documentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [ tags.DOCUMENTS, tags.WORKER, tags.WORKER_LIST ],
    }),
  }),
});

export const {
  useGetWorkersQuery,
  useGetWorkerByIdQuery,
  useCreateWorkerMutation,
  useUpdateWorkerMutation,
  useUploadWorkerPhotoMutation,
  useDeleteWorkerMutation,

  useGetEmployersQuery,
  useCreateEmployerMutation,

  useGetDocumentTypesQuery,
  useGetDocumentsQuery,
  useCreateDocumentMutation,
  useUpdateDocumentMutation,
  useUploadDocumentFileMutation,
  useDeleteDocumentMutation,
} = workersApi;
